import { useEffect, useState } from "react";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Printer, Check, X } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/pages/it/Aset";
import { DateFilter, matchDate, usePagination, printRowClass, PaginationNav, useRooms, PrintSignature } from "@/components/tableUtils";
import { useAuth } from "@/context/AuthContext";

export const formatRupiah = (n) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);

export function ProcurementStatusBadge({ status }) {
  const styles = {
    Diajukan: "bg-amber-100 text-amber-800 border-amber-200",
    Disetujui: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Ditolak: "bg-red-100 text-red-800 border-red-200",
  };
  return <Badge variant="outline" className={styles[status] || ""} data-testid={`proc-status-${status?.toLowerCase()}`}>{status}</Badge>;
}

function ReportShell({ title, subtitle, testid, children, filter, signature }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-heading font-semibold">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          {filter}
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid={`${testid}-print-button`}>
            <Printer className="w-4 h-4 mr-2" /> Cetak
          </Button>
        </div>
      </div>
      <div className="print-area bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="hidden print:block px-6 pt-6">
          <p className="font-heading font-bold text-lg">SIPAT — Sistem Informasi Pemeliharaan Aset TI</p>
          <p className="text-sm">{title}</p>
          <p className="text-xs mb-4">Tanggal cetak: {new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
        {children}
        {signature}
      </div>
    </section>
  );
}

export function ReportMaintenance() {
  const { user } = useAuth();
  const rooms = useRooms();
  const [room, setRoom] = useState("semua");
  const [logs, setLogs] = useState([]);
  const [mode, setMode] = useState("semua");
  const [value, setValue] = useState("");
  useEffect(() => { api.get("/reports/maintenance").then((r) => setLogs(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const filtered = logs.filter((l) => matchDate(l.tanggal, mode, value) && (room === "semua" || l.asset_ruangan === room));
  const { page, setPage, pageCount } = usePagination(filtered.length);
  const roomObj = rooms.find((r) => r.nama === room);
  return (
    <ReportShell title="Laporan Hasil Maintenance" subtitle="Seluruh pekerjaan maintenance yang dilakukan tim IT." testid="report-maintenance"
      filter={<DateFilter mode={mode} setMode={(m) => { setMode(m); setPage(1); }} value={value} setValue={(v) => { setValue(v); setPage(1); }} testid="report-maintenance-filter" rooms={rooms} room={room} setRoom={(r) => { setRoom(r); setPage(1); }} />}
      signature={room === "semua" ? null : <PrintSignature leftName={user?.username} leftNip={user?.nip} rightName={roomObj?.kepala_ruangan} rightNip={roomObj?.nip} />}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tanggal</TableHead>
            <TableHead>Kode</TableHead>
            <TableHead>Aset</TableHead>
            <TableHead>Ruangan</TableHead>
            <TableHead>Detail Pekerjaan</TableHead>
            <TableHead>Hasil</TableHead>
            <TableHead>Rekomendasi</TableHead>
            <TableHead>Teknisi</TableHead>
            <TableHead>Bukti Foto</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((l, i) => (
            <TableRow key={l.id} className={printRowClass(i, page)} data-testid={`report-maintenance-row-${l.id.slice(0, 8)}`}>
              <TableCell>{l.tanggal}</TableCell>
              <TableCell className="font-medium">{l.asset_kode}</TableCell>
              <TableCell>{l.asset_nama}</TableCell>
              <TableCell>{l.asset_ruangan}</TableCell>
              <TableCell className="max-w-xs">{l.detail_pekerjaan}</TableCell>
              <TableCell><StatusBadge status={l.status} /></TableCell>
              <TableCell className="max-w-xs">{l.rekomendasi_pembelian || "—"}</TableCell>
              <TableCell>{l.teknisi || "—"}</TableCell>
              <TableCell>
                {l.foto_bukti ? (
                  <a href={`${API}/public/files/${l.foto_bukti}`} target="_blank" rel="noopener noreferrer">
                    <img src={`${API}/public/files/${l.foto_bukti}`} alt="Bukti" className="w-12 h-12 object-cover rounded border border-border" data-testid={`foto-bukti-${l.id.slice(0, 8)}`} />
                  </a>
                ) : "—"}
              </TableCell>
            </TableRow>
          ))}
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Belum ada data maintenance.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <PaginationNav page={page} pageCount={pageCount} onChange={setPage} testid="report-maintenance-pagination" />
    </ReportShell>
  );
}

export function ReportAssets() {
  const { user } = useAuth();
  const rooms = useRooms();
  const [room, setRoom] = useState("semua");
  const [assets, setAssets] = useState([]);
  const [mode, setMode] = useState("semua");
  const [value, setValue] = useState("");
  useEffect(() => { api.get("/reports/aset").then((r) => setAssets(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const filtered = assets.filter((a) => matchDate(a.tanggal_pengadaan, mode, value) && (room === "semua" || a.ruangan === room));
  const { page, setPage, pageCount } = usePagination(filtered.length);
  const roomObj = rooms.find((r) => r.nama === room);
  return (
    <ReportShell title="Laporan Aset IT" subtitle="Seluruh aset peralatan IT di rumah sakit." testid="report-aset"
      filter={<DateFilter mode={mode} setMode={(m) => { setMode(m); setPage(1); }} value={value} setValue={(v) => { setValue(v); setPage(1); }} testid="report-aset-filter" rooms={rooms} room={room} setRoom={(r) => { setRoom(r); setPage(1); }} />}
      signature={room === "semua" ? null : <PrintSignature leftName={user?.username} leftNip={user?.nip} rightName={roomObj?.kepala_ruangan} rightNip={roomObj?.nip} />}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Kode</TableHead>
            <TableHead>Nama Aset</TableHead>
            <TableHead>Kategori</TableHead>
            <TableHead>Ruangan</TableHead>
            <TableHead>Merk</TableHead>
            <TableHead>Pengadaan</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Aktif</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((a, i) => (
            <TableRow key={a.id} className={printRowClass(i, page)} data-testid={`report-aset-row-${a.kode}`}>
              <TableCell className="font-medium">{a.kode}</TableCell>
              <TableCell>{a.nama}</TableCell>
              <TableCell>{a.kategori}</TableCell>
              <TableCell>{a.ruangan}</TableCell>
              <TableCell>{a.merk}</TableCell>
              <TableCell>{a.tanggal_pengadaan || "—"}</TableCell>
              <TableCell><StatusBadge status={a.status} /></TableCell>
              <TableCell>{a.is_active ? "Aktif" : "Nonaktif"}</TableCell>
            </TableRow>
          ))}
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Belum ada aset.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <PaginationNav page={page} pageCount={pageCount} onChange={setPage} testid="report-aset-pagination" />
    </ReportShell>
  );
}

export function ReportRepair() {
  const { user } = useAuth();
  const rooms = useRooms();
  const [room, setRoom] = useState("semua");
  const [data, setData] = useState({ assets: [], rekomendasi: [] });
  const [mode, setMode] = useState("semua");
  const [value, setValue] = useState("");
  useEffect(() => { api.get("/reports/perbaikan").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const filtered = data.assets.filter((a) => matchDate(a.tanggal_pengadaan, mode, value) && (room === "semua" || a.ruangan === room));
  const { page, setPage, pageCount } = usePagination(filtered.length);
  const roomObj = rooms.find((r) => r.nama === room);
  return (
    <ReportShell title="Laporan Aset Dalam Perbaikan" subtitle="Aset yang sedang diperbaiki atau berstatus rusak." testid="report-perbaikan"
      filter={<DateFilter mode={mode} setMode={(m) => { setMode(m); setPage(1); }} value={value} setValue={(v) => { setValue(v); setPage(1); }} testid="report-perbaikan-filter" rooms={rooms} room={room} setRoom={(r) => { setRoom(r); setPage(1); }} />}
      signature={room === "semua" ? null : <PrintSignature leftName={user?.username} leftNip={user?.nip} rightName={roomObj?.kepala_ruangan} rightNip={roomObj?.nip} />}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Kode</TableHead>
            <TableHead>Nama Aset</TableHead>
            <TableHead>Ruangan</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((a, i) => (
            <TableRow key={a.id} className={printRowClass(i, page)} data-testid={`report-perbaikan-row-${a.kode}`}>
              <TableCell className="font-medium">{a.kode}</TableCell>
              <TableCell>{a.nama}</TableCell>
              <TableCell>{a.ruangan}</TableCell>
              <TableCell><StatusBadge status={a.status} /></TableCell>
            </TableRow>
          ))}
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-10">Tidak ada aset dalam perbaikan.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <PaginationNav page={page} pageCount={pageCount} onChange={setPage} testid="report-perbaikan-pagination" />
    </ReportShell>
  );
}

export function ReportRecommendations() {
  const { user } = useAuth();
  const rooms = useRooms();
  const [room, setRoom] = useState("semua");
  const [data, setData] = useState({ assets: [], rekomendasi: [] });
  const [mode, setMode] = useState("semua");
  const [value, setValue] = useState("");
  useEffect(() => { api.get("/reports/perbaikan").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const filtered = data.rekomendasi.filter((l) => matchDate(l.tanggal, mode, value) && (room === "semua" || l.asset_ruangan === room));
  const { page, setPage, pageCount } = usePagination(filtered.length);
  const roomObj = rooms.find((r) => r.nama === room);
  return (
    <ReportShell title="Laporan Rekomendasi Perbaikan & Pembelian" subtitle="Rekomendasi dari hasil maintenance tim IT." testid="report-rekomendasi"
      filter={<DateFilter mode={mode} setMode={(m) => { setMode(m); setPage(1); }} value={value} setValue={(v) => { setValue(v); setPage(1); }} testid="report-rekomendasi-filter" rooms={rooms} room={room} setRoom={(r) => { setRoom(r); setPage(1); }} />}
      signature={room === "semua" ? null : <PrintSignature leftName={user?.username} leftNip={user?.nip} rightName={roomObj?.kepala_ruangan} rightNip={roomObj?.nip} />}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tanggal</TableHead>
            <TableHead>Kode</TableHead>
            <TableHead>Aset</TableHead>
            <TableHead>Ruangan</TableHead>
            <TableHead>Hasil Maintenance</TableHead>
            <TableHead>Rekomendasi</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((l, i) => (
            <TableRow key={l.id} className={printRowClass(i, page)} data-testid={`report-rekomendasi-row-${l.id.slice(0, 8)}`}>
              <TableCell>{l.tanggal}</TableCell>
              <TableCell className="font-medium">{l.asset_kode}</TableCell>
              <TableCell>{l.asset_nama}</TableCell>
              <TableCell>{l.asset_ruangan}</TableCell>
              <TableCell><StatusBadge status={l.status} /></TableCell>
              <TableCell className="max-w-sm">{l.rekomendasi_pembelian}</TableCell>
            </TableRow>
          ))}
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Belum ada rekomendasi dari maintenance.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <PaginationNav page={page} pageCount={pageCount} onChange={setPage} testid="report-rekomendasi-pagination" />
    </ReportShell>
  );
}

export function ReportPengadaan({ canApprove = false }) {
  const { user } = useAuth();
  const rooms = useRooms();
  const [room, setRoom] = useState("semua");
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState("semua");
  const [value, setValue] = useState("");
  const load = () => api.get("/reports/pengadaan").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const filtered = items.filter((p) => matchDate(p.created_at, mode, value) && (room === "semua" || p.asset_ruangan === room));
  const { page, setPage, pageCount } = usePagination(filtered.length);
  const roomObj = rooms.find((r) => r.nama === room);

  const setStatus = async (p, status) => {
    try {
      await api.patch(`/procurements/${p.id}/status`, { status });
      toast.success(`Rekomendasi ${status.toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <ReportShell title="Laporan Rekomendasi" subtitle="Rekomendasi aset yang diajukan untuk dibeli / diperbaiki." testid="report-pengadaan"
      filter={<DateFilter mode={mode} setMode={(m) => { setMode(m); setPage(1); }} value={value} setValue={(v) => { setValue(v); setPage(1); }} testid="report-pengadaan-filter" rooms={rooms} room={room} setRoom={(r) => { setRoom(r); setPage(1); }} />}
      signature={room === "semua" ? null : <PrintSignature leftName={user?.username} leftNip={user?.nip} rightName={roomObj?.kepala_ruangan} rightNip={roomObj?.nip} />}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Barang</TableHead>
            <TableHead>Jenis</TableHead>
            <TableHead>Jumlah</TableHead>
            <TableHead>Estimasi Harga</TableHead>
            <TableHead>Spesifikasi</TableHead>
            <TableHead>Diajukan Oleh</TableHead>
            <TableHead>Status</TableHead>
            {canApprove && <TableHead className="text-right">Aksi</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((p, i) => (
            <TableRow key={p.id} className={printRowClass(i, page)} data-testid={`report-pengadaan-row-${p.id.slice(0, 8)}`}>
              <TableCell className="font-medium">{p.nama_barang}</TableCell>
              <TableCell>{p.jenis}</TableCell>
              <TableCell>{p.jumlah}</TableCell>
              <TableCell>{formatRupiah(p.estimasi_harga)}</TableCell>
              <TableCell className="max-w-xs truncate">{p.spesifikasi || "—"}</TableCell>
              <TableCell>{p.diajukan_oleh}</TableCell>
              <TableCell><ProcurementStatusBadge status={p.status} /></TableCell>
              {canApprove && (
                <TableCell className="text-right">
                  {p.status === "Diajukan" && (
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setStatus(p, "Disetujui")} title="Setujui" data-testid={`approve-${p.id.slice(0, 8)}`}>
                        <Check className="w-4 h-4 text-primary" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setStatus(p, "Ditolak")} title="Tolak" data-testid={`reject-${p.id.slice(0, 8)}`}>
                        <X className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={canApprove ? 8 : 7} className="text-center text-muted-foreground py-10">Belum ada data rekomendasi.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <PaginationNav page={page} pageCount={pageCount} onChange={setPage} testid="report-pengadaan-pagination" />
    </ReportShell>
  );
}
